import http from "./httpService";

/**
 * @typedef {import('../types/api.js').BaseApiResponse} BaseApiResponse
 * @typedef {import('../types/api.js').QuoteLogParams} QuoteLogParams
 * @typedef {import('../types/api.js').QuoteLogsData} QuoteLogsData
 * @typedef {import('../types/api.js').DownloadedQuoteParams} DownloadedQuoteParams
 * @typedef {import('../types/api.js').DownloadedQuoteData} DownloadedQuoteData
 * @typedef {import('../types/api.js').SessionCountParams} SessionCountParams
 * @typedef {import('../types/api.js').SessionCountData} SessionCountData
 * @typedef {import('../types/api.js').CartLogsParams} CartLogsParams
 * @typedef {import('../types/api.js').CartLogsData} CartLogsData
 * @typedef {import('../types/api.js').ExportCartLogsParams} ExportCartLogsParams
 * @typedef {import('../types/api.js').StartSessionRequest} StartSessionRequest
 * @typedef {import('../types/api.js').EndSessionRequest} EndSessionRequest
 */

class MakerCartLogService {
  /**
   * Get cart logs with pagination and filtering
   * @param {CartLogsParams} params - Query parameters for filtering and pagination
   * @returns {Promise<BaseApiResponse & { data: CartLogsData }>} Cart logs response
   */
  async getCartLogs(params) {
    const url = http.getUrlWithParams("/cart-logs", params);
    const response = await http.get(url);
    return response.data;
  }

  /**
   * Export cart logs as CSV file
   * @param {ExportCartLogsParams} params - Query parameters for filtering
   * @returns {Promise<Blob>} CSV file blob
   */
  async exportCartLogs(params) {
    const url = http.getUrlWithParams("/cart-logs/export-csv", params);
    const response = await http.get(url, {
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Export quotes logs as CSV file
   * @param {ExportCartLogsParams} params - Query parameters for filtering
   * @returns {Promise<Blob>} CSV file blob
   */
  async exportQuotesLogs(params) {
    const url = http.getUrlWithParams("/quotes-logs/export-csv", params);
    const response = await http.get(url, {
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Get quote logs with pagination and filtering
   * @param {QuoteLogParams} params - Query parameters for filtering and pagination
   * @returns {Promise<BaseApiResponse & { data: QuoteLogsData }>} Quote logs response
   */
  async getQuoteLog(params) {
    const url = http.getUrlWithParams("/quotes-logs", params);
    const response = await http.get(url);
    return response.data;
  }

  /**
   * Get quote preview PDF by quote code
   * @param {string} quoteCode - Quote code to preview
   * @returns {Promise<Blob>} Quote preview PDF blob
   */
  async previewQuote(quoteCode) {
    const response = await http.post(
      `/user-cart-quotes/preview/${encodeURIComponent(quoteCode)}`,
      undefined,
      { responseType: "blob" }
    );
    return response.data;
  }

  /**
   * Get downloaded quotes count statistics
   * @param {DownloadedQuoteParams} data - Query parameters for filtering
   * @returns {Promise<BaseApiResponse & { data: DownloadedQuoteData }>} Downloaded quotes count response
   */
  async getDownloadedQuote(data) {
    const response = await http.get("/downloaded-quotes-count", {
      params: data,
    });
    return response.data;
  }

  /**
   * Get session count statistics with step-by-step data
   * @param {SessionCountParams} data - Query parameters for pagination and sorting
   * @returns {Promise<BaseApiResponse & { data: SessionCountData }>} Session count response
   */
  async getSessionCount(params) {
    const url = http.getUrlWithParams("/session-count", params);
    const response = await http.get(url);
    return response.data;
  }

  /**
   * Start a new session
   * @param {StartSessionRequest} data - Session data
   * @returns {Promise<BaseApiResponse>} Start session response
   */
  async startSession(data) {
    const response = await http.post("/cart-logs", data);
    return response.data;
  }

  /**
   * End a session
   * @param {EndSessionRequest} data - Session data
   * @returns {Promise<BaseApiResponse>} End session response
   */
  async endSession(data) {
    const response = await http.put("/cart-logs/drop-off", data);
    return response.data;
  }
}

export default new MakerCartLogService();
